import React, { useEffect, useState } from 'react';
import { getGame, scoreNumber } from '../../lib/minigames';

// Live-game wrapper. Mounts when a challenge involving the current player is
// in `active` or `completed` status. Renders a 3-2-1 countdown, the active
// minigame Component, then the result screen once both scores land.

const COUNTDOWN_SEC = 3;

export default function MinigameOverlay({
  challenge,
  myPlayerId,
  onSubmitScore,           // (score) => Promise
  onClose,
}) {
  if (!challenge) return null;
  const game = getGame(challenge.gameId);
  if (!game) return null;

  const mySide   = challenge.fromPlayerId === myPlayerId ? 'from' : 'to';
  const myParams = mySide === 'from' ? challenge.fromParams : challenge.toParams;
  const myScore  = mySide === 'from' ? challenge.fromScore  : challenge.toScore;
  const opponentName = mySide === 'from' ? challenge.toName : challenge.fromName;
  const opponentScore = mySide === 'from' ? challenge.toScore : challenge.fromScore;

  // Local UI flow:
  //   countdown → playing → submitted (waiting for opponent) → done (when status=completed)
  const [phase, setPhase] = useState('countdown');
  const [count, setCount] = useState(COUNTDOWN_SEC);

  useEffect(() => {
    if (phase !== 'countdown') return;
    if (count <= 0) {
      setPhase('playing');
      return;
    }
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, count]);

  // If we already submitted a score (e.g. reload), skip past playing.
  useEffect(() => {
    if (myScore != null && phase === 'playing') setPhase('submitted');
  }, [myScore, phase]);

  // Once challenge.status flips to 'completed', show the result screen.
  useEffect(() => {
    if (challenge.status === 'completed') setPhase('done');
  }, [challenge.status]);

  const handleScore = async (score) => {
    if (phase !== 'playing') return;
    setPhase('submitted');
    await onSubmitScore?.(score);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <span style={styles.title}>{game.icon} {game.label}</span>
          {phase === 'done' && onClose && (
            <button style={styles.closeBtn} onClick={onClose}>✕</button>
          )}
        </div>

        <div style={styles.body}>
          {phase === 'countdown' && (
            <div style={styles.countdown}>
              <div style={styles.countdownLabel}>Get ready</div>
              <div style={styles.countdownNum}>{count > 0 ? count : 'GO!'}</div>
              <div style={styles.countdownVs}>vs {opponentName}</div>
            </div>
          )}

          {phase === 'playing' && (
            <game.Component
              challenge={challenge}
              mySide={mySide}
              params={myParams}
              seed={challenge.seed}
              onScore={handleScore}
            />
          )}

          {phase === 'submitted' && (
            <div style={styles.waiting}>
              <div style={styles.waitingTitle}>Score submitted</div>
              <div style={styles.waitingScore}>Your score: <strong>{scoreNumber(challenge.gameId, myScore) ?? '—'}</strong></div>
              <div style={styles.waitingHint}>
                {opponentScore != null
                  ? `${opponentName} scored ${scoreNumber(challenge.gameId, opponentScore)} — finalizing…`
                  : `Waiting for ${opponentName}…`}
              </div>
            </div>
          )}

          {phase === 'done' && (
            <ResultScreen challenge={challenge} mySide={mySide} game={game} />
          )}
        </div>

        {phase === 'done' && (
          <div style={styles.footer}>
            <button style={styles.closeBigBtn} onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultScreen({ challenge, mySide, game }) {
  const myScore       = mySide === 'from' ? challenge.fromScore : challenge.toScore;
  const opponentScore = mySide === 'from' ? challenge.toScore   : challenge.fromScore;
  const opponentName  = mySide === 'from' ? challenge.toName    : challenge.fromName;
  const youWon        = challenge.winner === mySide;
  const tie           = challenge.winner === 'tie';
  const headline      = tie ? '🤝 Draw' : youWon ? '🏆 Victory' : '💀 Defeat';
  const ResultDisplay = game.ResultDisplay;
  const tiebroken     = challenge.tiebreaker === 'stat';
  const statLabel     = (challenge.stat || '').toUpperCase();
  const tiebreakText  = tiebroken
    ? (youWon ? `Tied score — edged out on ${statLabel}` : `Tied score — opponent edged out on ${statLabel}`)
    : null;

  return (
    <div style={styles.result}>
      <div style={{ ...styles.resultHeadline, color: tie ? '#9a8b6e' : youWon ? '#d4a847' : '#f87171' }}>
        {headline}
      </div>
      {tiebreakText && <div style={styles.tiebreakBadge}>⚖ {tiebreakText}</div>}
      {ResultDisplay ? (
        <ResultDisplay challenge={challenge} mySide={mySide} />
      ) : (
        <div style={styles.scoreboard}>
          <div style={{ ...styles.scoreCell, ...(youWon ? styles.scoreCellWin : {}) }}>
            <div style={styles.scoreLabel}>You</div>
            <div style={styles.scoreVal}>{scoreNumber(challenge.gameId, myScore) ?? '—'}</div>
          </div>
          <div style={styles.vs}>vs</div>
          <div style={{ ...styles.scoreCell, ...(challenge.winner === (mySide === 'from' ? 'to' : 'from') ? styles.scoreCellWin : {}) }}>
            <div style={styles.scoreLabel}>{opponentName}</div>
            <div style={styles.scoreVal}>{scoreNumber(challenge.gameId, opponentScore) ?? '—'}</div>
          </div>
        </div>
      )}
      <div style={styles.resultDesc}>{game.description}</div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  panel:   { width: '100%', maxWidth: 480, maxHeight: '92vh', background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #d4a847', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', overflow: 'hidden', color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #4a3d28', background: 'rgba(0,0,0,0.2)' },
  title:   { fontSize: 16, fontWeight: 800, color: '#d4a847', letterSpacing: '0.04em' },
  closeBtn:{ background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 20, cursor: 'pointer', padding: '0 4px' },

  body:    { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' },

  countdown:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 36 },
  countdownLabel: { fontSize: 13, color: '#9a8b6e', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 },
  countdownNum:   { fontSize: 72, fontWeight: 800, color: '#d4a847', textShadow: '0 4px 20px rgba(212,168,71,0.5)' },
  countdownVs:    { fontSize: 13, color: '#9a8b6e', fontStyle: 'italic' },

  waiting:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 30 },
  waitingTitle: { fontSize: 14, fontWeight: 700, color: '#d4a847', letterSpacing: '0.06em', textTransform: 'uppercase' },
  waitingScore: { fontSize: 22, color: '#f0e6d0', fontWeight: 700 },
  waitingHint:  { fontSize: 12, color: '#9a8b6e', fontStyle: 'italic' },

  result:         { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: 24 },
  resultHeadline: { fontSize: 32, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.06em', textShadow: '0 4px 14px rgba(0,0,0,0.5)' },
  scoreboard:     { display: 'flex', alignItems: 'stretch', gap: 12 },
  scoreCell:      { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 18px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, minWidth: 100 },
  scoreCellWin:   { borderColor: '#d4a847', background: 'rgba(212,168,71,0.1)', boxShadow: '0 0 16px rgba(212,168,71,0.4)' },
  scoreLabel:     { fontSize: 11, color: '#9a8b6e', fontWeight: 700, letterSpacing: '0.06em' },
  scoreVal:       { fontSize: 28, fontWeight: 800, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace" },
  vs:             { display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 800, color: '#9a8b6e' },
  resultDesc:     { fontSize: 12, color: '#9a8b6e', textAlign: 'center', maxWidth: 320, fontStyle: 'italic' },
  tiebreakBadge:  { fontSize: 11, fontWeight: 700, color: '#7ab8e0', background: 'rgba(122,184,224,0.12)', border: '1px solid rgba(122,184,224,0.4)', borderRadius: 4, padding: '4px 10px', letterSpacing: '0.04em' },

  footer:    { display: 'flex', justifyContent: 'flex-end', padding: '10px 14px', borderTop: '1px solid #4a3d28', background: 'rgba(0,0,0,0.18)' },
  closeBigBtn: { background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', color: '#1a0e00', padding: '8px 24px', fontSize: 13, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 4 },
};
